/**
 * Sanctuary API Client
 *
 * Handles all API calls to the Sanctuary backend.
 */

import { getApiToken } from './auth'

const API_URL = import.meta.env.VITE_API_URL || '/api'

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = await getApiToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

// Stats
export async function getStats() {
  return fetchWithAuth('/stats')
}

// Soft Rooms
export async function getSoftRooms() {
  return fetchWithAuth('/soft-rooms')
}

export async function getSoftRoom(id: string) {
  return fetchWithAuth(`/soft-rooms/${id}`)
}

export async function createSoftRoom(data: Record<string, unknown>) {
  return fetchWithAuth('/soft-rooms', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function unlockSafe(roomId: string) {
  return fetchWithAuth(`/soft-rooms/${roomId}/unlock-safe`, {
    method: 'POST',
  })
}

// Field Reports
export async function getFieldReports(params?: { status?: string; airport?: string }) {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  if (params?.airport) searchParams.set('airport', params.airport)
  const query = searchParams.toString()
  return fetchWithAuth(`/field-reports${query ? `?${query}` : ''}`)
}

export async function createFieldReport(data: Record<string, unknown>) {
  return fetchWithAuth('/field-reports', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateReportStatus(reportId: string, status: string, notes?: string) {
  const searchParams = new URLSearchParams({ status })
  if (notes) searchParams.set('notes', notes)
  return fetchWithAuth(`/field-reports/${reportId}/status?${searchParams}`, {
    method: 'PATCH',
  })
}

// Sessions
export async function getSessions(params?: { status?: string }) {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  const query = searchParams.toString()
  return fetchWithAuth(`/sessions${query ? `?${query}` : ''}`)
}

export async function getSession(sessionId: string) {
  return fetchWithAuth(`/sessions/${sessionId}`)
}

export async function createSession(data: Record<string, unknown>) {
  return fetchWithAuth('/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function completeSession(sessionId: string, notes?: string) {
  const searchParams = new URLSearchParams()
  if (notes) searchParams.set('notes', notes)
  return fetchWithAuth(`/sessions/${sessionId}/complete?${searchParams}`, {
    method: 'POST',
  })
}

// Services
export async function addService(data: Record<string, unknown>) {
  return fetchWithAuth('/services', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function getSessionServices(sessionId: string) {
  return fetchWithAuth(`/sessions/${sessionId}/services`)
}

// Partners
export async function getPartners(partnerType?: string) {
  const query = partnerType ? `?partner_type=${partnerType}` : ''
  return fetchWithAuth(`/partners${query}`)
}

export async function createPartner(data: Record<string, unknown>) {
  return fetchWithAuth('/partners', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// Clients (Clarity integration)
export async function searchClients(search: string) {
  return fetchWithAuth(`/clients?search=${encodeURIComponent(search)}`)
}

// Auth
export async function getCurrentUser() {
  return fetchWithAuth('/auth/me')
}
